/**
 * React Query hooks for Platform Admin Tools Management
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPlatformAdminTools,
  getPlatformAdminToolStats,
  getPlatformAdminTool,
  createPlatformTool,
  updatePlatformTool,
  deletePlatformTool,
  toggleToolFeatured,
  getToolCategories,
  createToolCategory,
} from "@services/platformAdminTools";
import type {
  PlatformToolFilters,
  PlatformToolCreate,
  PlatformToolUpdate,
  ToolFeatureToggle,
  ToolCategoryCreate,
} from "@/types/adminTools";

// Query keys
export const platformAdminToolsKeys = {
  all: ["platform-admin-tools"] as const,
  lists: () => [...platformAdminToolsKeys.all, "list"] as const,
  list: (filters?: PlatformToolFilters) =>
    [...platformAdminToolsKeys.lists(), filters] as const,
  stats: () => [...platformAdminToolsKeys.all, "stats"] as const,
  details: () => [...platformAdminToolsKeys.all, "detail"] as const,
  detail: (id: string) => [...platformAdminToolsKeys.details(), id] as const,
  categories: () => [...platformAdminToolsKeys.all, "categories"] as const,
};

/**
 * Hook to fetch platform admin tools with filters
 */
export function usePlatformAdminTools(filters?: PlatformToolFilters) {
  return useQuery({
    queryKey: platformAdminToolsKeys.list(filters),
    queryFn: () => getPlatformAdminTools(filters),
  });
}

/**
 * Hook to fetch platform admin tool stats
 */
export function usePlatformAdminToolStats() {
  return useQuery({
    queryKey: platformAdminToolsKeys.stats(),
    queryFn: getPlatformAdminToolStats,
  });
}

/**
 * Hook to fetch a single tool
 */
export function usePlatformAdminTool(toolId: string) {
  return useQuery({
    queryKey: platformAdminToolsKeys.detail(toolId),
    queryFn: () => getPlatformAdminTool(toolId),
    enabled: !!toolId,
  });
}

/**
 * Hook to create a public tool
 */
export function useCreatePlatformTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PlatformToolCreate) => createPlatformTool(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformAdminToolsKeys.all });
    },
  });
}

/**
 * Hook to update a tool
 */
export function useUpdatePlatformTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toolId,
      data,
    }: {
      toolId: string;
      data: PlatformToolUpdate;
    }) => updatePlatformTool(toolId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: platformAdminToolsKeys.all });
      queryClient.invalidateQueries({
        queryKey: platformAdminToolsKeys.detail(variables.toolId),
      });
    },
  });
}

/**
 * Hook to delete a tool
 */
export function useDeletePlatformTool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (toolId: string) => deletePlatformTool(toolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: platformAdminToolsKeys.all });
    },
  });
}

/**
 * Hook to toggle tool featured status
 */
export function useToggleToolFeatured() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      toolId,
      data,
    }: {
      toolId: string;
      data: ToolFeatureToggle;
    }) => toggleToolFeatured(toolId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: platformAdminToolsKeys.all });
      queryClient.invalidateQueries({
        queryKey: platformAdminToolsKeys.detail(variables.toolId),
      });
    },
  });
}

/**
 * Hook to fetch tool categories
 */
export function useToolCategories() {
  return useQuery({
    queryKey: platformAdminToolsKeys.categories(),
    queryFn: getToolCategories,
  });
}

/**
 * Hook to create a tool category
 */
export function useCreateToolCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ToolCategoryCreate) => createToolCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: platformAdminToolsKeys.categories(),
      });
    },
  });
}
